import React, { useEffect, useState } from "react";
import { buildApiUrl } from "./config";
import StudentMonitor_webrtc from "./StudentMonitor_webrtc";
import ChatWindow from "./ChatWindow";
import { useAuth } from "./AuthContext";
import { getYearLabel } from './utils/yearUtils';
import { LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import axios from "axios";

// ── Copy shareable docs link button ─────────────────────────────────────────
const CopyDocLinkButton = ({ studentId }) => {
  const [copied, setCopied] = React.useState(false);
  const url = `${window.location.origin}/docs/${studentId}`;
  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors ${copied ? 'bg-green-50 text-green-600 border-green-200' : 'bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50'}`}
    >
      {copied ? (
        <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Copied!</>
      ) : (
        <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Copy Share Link</>
      )}
    </button>
  );
};

// ── Parent Documents tab shown inside School Manager student profile ──────────
const DOC_LABELS = {
  pupil_passport:    'Pupil Passport',
  father_passport:   'Father Passport',
  mother_passport:   'Mother Passport',
  pupil_qid:         'Pupil QID',
  father_qid:        'Father QID',
  mother_qid:        'Mother QID',
  pupil_immunization:'Pupil Immunization',
  eyfs_form:         'EYFS Form',
};

const DocumentsTab = ({ studentId }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!studentId) return;
    axios.get(buildApiUrl(`/api/parent/documents/${studentId}`))
      .then(res => { if (res.data.success) setDocs(res.data.documents || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [studentId]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleDateString('en-GB');
  };

  const isExpired = (iso) => iso && new Date(iso) < new Date();

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="mt-4 space-y-4">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between border-b pb-2 mb-4">
          <h3 className="text-lg font-bold text-gray-900">📁 Parent Uploaded Documents</h3>
          <CopyDocLinkButton studentId={studentId} />
        </div>
        {docs.length === 0 ? (
          <p className="text-gray-400 text-sm">No documents uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b">
                  <th className="pb-2 pr-4">Document</th>
                  <th className="pb-2 pr-4">File</th>
                  <th className="pb-2 pr-4">Expiry</th>
                  <th className="pb-2 pr-4">Uploaded</th>
                  <th className="pb-2">iSAMS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {docs.map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="py-2.5 pr-4 font-medium text-gray-800">
                      {DOC_LABELS[doc.doc_type] || doc.doc_type}
                    </td>
                    <td className="py-2.5 pr-4">
                      <a
                        href={buildApiUrl(`/api/parent/documents/download/${doc.id}`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-xs"
                      >
                        {doc.original_name || 'Download'}
                      </a>
                    </td>
                    <td className={`py-2.5 pr-4 font-medium ${isExpired(doc.expiry_date) ? 'text-red-500' : 'text-gray-700'}`}>
                      {formatDate(doc.expiry_date)}
                      {isExpired(doc.expiry_date) && <span className="ml-1 text-xs font-bold text-red-500">EXPIRED</span>}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-500 text-xs">
                      {formatDate(doc.uploaded_at)}
                    </td>
                    <td className="py-2.5">
                      {doc.isams_synced
                        ? <span className="text-xs font-semibold text-indigo-500">✓ Synced</span>
                        : <span className="text-xs font-semibold text-amber-500">⚠ Pending</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const StudentPage = ({ studentId: propStudentId }) => {
  // Get studentId from prop first, then from URL if not provided
  let studentId = propStudentId;
  if (!studentId) {
    const match = window.location.pathname.match(/student\/([^/]+)/);
    if (match) studentId = match[1];
  }

  // Get activeTab from history.state if available
  let initialTab = "core-data";
  try {
    if (window.history.state && window.history.state.usr && window.history.state.usr.activeTab) {
      initialTab = window.history.state.usr.activeTab;
    }
  } catch {}

  const [student, setStudent] = useState({});
  const [tab, setTab] = useState(initialTab);
  const [selectedModule, setSelectedModule] = useState("AR Reading Age");
  const [studentAssignments, setStudentAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [bmiData, setBmiData] = useState([]);
  const [readingAgeData, setReadingAgeData] = useState([]);
  const [cambridgeAssessmentData, setCambridgeAssessmentData] = useState(null);
  const [cambridgeAssessmentData2024, setCambridgeAssessmentData2024] = useState(null);
  const [cambridgeAssessmentData2023, setCambridgeAssessmentData2023] = useState(null);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("2025-2026");
  const [expandedModules, setExpandedModules] = useState({});
  const [medicalFields, setMedicalFields] = useState({
    consentFormSigned: null,
    hasMedicalCondition: null,
    medicalConditionNotes: ""
  });
  const [rewardsData, setRewardsData] = useState({ total_points: 0, reward_level: null });
  const [attendanceScore, setAttendanceScore] = useState(null);
  const [notes, setNotes] = useState([]);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");

  // ── Student Targets ──────────────────────────────────────────────────────────
  const [targets, setTargets] = useState([]);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [targetModalModule, setTargetModalModule] = useState(null);   // {name, score}
  const [targetModalView, setTargetModalView] = useState(null);       // 'choose' | 'create' | 'link'
  const [targetForm, setTargetForm] = useState({ title: '', description: '', target_score: '', due_date: '' });
  const [targetLinkId, setTargetLinkId] = useState(null);
  const [targetSaving, setTargetSaving] = useState(false);

  const fetchTargets = (sid) => {
    fetch(buildApiUrl(`/api/student-targets?student_id=${sid}`))
      .then(r => r.json())
      .then(r => { if (r.success) setTargets(r.targets); })
      .catch(() => {});
  };

  const openTargetModal = (moduleName, score) => {
    setTargetModalModule({ name: moduleName, score });
    setTargetModalView('choose');
    setTargetForm({ title: `${moduleName} Target`, description: '', target_score: '', due_date: '' });
    setTargetLinkId(null);
    setShowTargetModal(true);
  };

  const saveNewTarget = async () => {
    setTargetSaving(true);
    try {
      const res = await fetch(buildApiUrl('/api/student-targets'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_school_id: student.school_id,
          title: targetForm.title,
          description: targetForm.description,
          assessment_module: targetModalModule?.name,
          current_score: String(targetModalModule?.score ?? ''),
          target_score: targetForm.target_score,
          due_date: targetForm.due_date || null,
          created_by: 'admin',
        }),
      });
      const data = await res.json();
      if (data.success) {
        fetchTargets(student.school_id);
        setShowTargetModal(false);
      } else {
        alert('Failed to save target: ' + data.error);
      }
    } finally {
      setTargetSaving(false);
    }
  };

  const linkAssessmentToTarget = async () => {
    if (!targetLinkId) return;
    setTargetSaving(true);
    try {
      const res = await fetch(buildApiUrl(`/api/student-targets/${targetLinkId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assessment_module: targetModalModule?.name,
          current_score: String(targetModalModule?.score ?? ''),
        }),
      });
      const data = await res.json();
      if (data.success) {
        fetchTargets(student.school_id);
        setShowTargetModal(false);
      } else {
        alert('Failed to link: ' + data.error);
      }
    } finally {
      setTargetSaving(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────


  const server = buildApiUrl("");

  useEffect(() => {
    fetch(buildApiUrl(`/synced-students`))
      .then(res => res.json())
      .then(res => {
        const match = res.students.find((s) => s.school_id === studentId);
        if (match) setStudent(match);
      });

    fetch(buildApiUrl(`/student-homework/${studentId}`))
      .then(res => res.json())
      .then(res => {
        if (res.homework) {
          setStudentAssignments(res.homework);
        }
      });

    // Fetch BMI data (height and weight over time)
    fetch(buildApiUrl(`/api/medical/students/${studentId}/bmi`))
      .then(res => res.json())
      .then(data => {
        if (data.measurements && Array.isArray(data.measurements)) {
          // Sort by date and format for charts
          const sortedData = data.measurements
            .map(m => ({
              date: new Date(m.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
              fullDate: new Date(m.date),
              height: parseFloat(m.height) || null,
              weight: parseFloat(m.weight) || null
            }))
            .sort((a, b) => a.fullDate - b.fullDate);
          
          setBmiData(sortedData);
          console.log('📊 BMI data loaded:', sortedData.length, 'measurements');
        }
      })
      .catch(err => {
        console.error('Error loading BMI data:', err);
      });

    // Fetch medical custom fields
    fetch(buildApiUrl(`/api/students/${studentId}/customFields`))
      .then(res => res.json())
      .then(data => {
        console.log('🔍 Raw API response:', JSON.stringify(data, null, 2));
        
        const fields = data.customFields || [];
        
        console.log('📋 Total custom fields received:', fields.length);
        console.log('📋 All fields:', fields);
        
        // Extract specific medical fields by ID (check both number and string)
        const consentField = fields.find(f => f.id === 40 || f.id === "40");
        const conditionField = fields.find(f => f.id === 1079 || f.id === "1079");
        const notesField = fields.find(f => f.id === 1080 || f.id === "1080");
        
        console.log('🔍 Consent field:', consentField);
        console.log('🔍 Consent value type:', typeof consentField?.value);
        console.log('🔍 Consent value:', JSON.stringify(consentField?.value));
        console.log('🔍 Consent value (raw):', consentField?.value);
        
        // Parse boolean values more reliably
        const parseBoolean = (value) => {
          if (value === true || value === "true" || value === "True" || value === "TRUE") {
            return true;
          }
          if (typeof value === 'string' && value.trim().toLowerCase() === 'true') {
            return true;
          }
          return false;
        };
        
        setMedicalFields({
          consentFormSigned: parseBoolean(consentField?.value),
          hasMedicalCondition: parseBoolean(conditionField?.value),
          medicalConditionNotes: notesField?.value || ""
        });
        
        console.log('✅ Parsed values:', {
          consentFormSigned: parseBoolean(consentField?.value),
          hasMedicalCondition: parseBoolean(conditionField?.value),
          notes: notesField?.value || ""
        });
      })
      .catch(err => {
        console.error('Error loading medical custom fields:', err);
      });

    // Load teachers for the student's form
    fetch(buildApiUrl(`/synced-students`))
      .then(res => res.json())
      .then(res => {
        const match = res.students.find((s) => s.school_id === studentId);
        if (match && match.form) {
          // Fetch teachers assigned to this form using the new endpoint
          fetch(buildApiUrl(`/staff/by-form/${match.form}`))
            .then(res => res.json())
            .then(data => {
              const formTeachers = data.staff || [];
              setTeachers(formTeachers);
              console.log(`✅ Loaded ${formTeachers.length} teachers for form ${match.form}`);
            })
            .catch(err => {
              console.error('Error loading teachers:', err);
              setTeachers([]);
            });
        }
      });
  }, [studentId]);

  // Fetch rewards data
  useEffect(() => {
    if (!studentId) return;
    fetchTargets(studentId);
  }, [studentId]);

  // Fetch rewards data
  useEffect(() => {
    if (!studentId) return;

    fetch(buildApiUrl(`/api/rewards/student/${studentId}/history`))
      .then(res => res.json())
      .then(data => {
        setRewardsData({
          total_points: data.total_points || 0,
          reward_level: data.reward_level || null
        });
      })
      .catch(err => {
        console.error('Error loading rewards data:', err);
      });
  }, [studentId]);

  // Fetch attendance score
  useEffect(() => {
    if (!studentId) return;

    fetch(buildApiUrl(`/attendance/student-score/${studentId}`))
      .then(res => res.json())
      .then(data => {
        if (data.success && data.attendance_percentage !== null) {
          setAttendanceScore(data);
        }
      })
      .catch(err => {
        console.error('Error loading attendance score:', err);
      });
  }, [studentId]);

  // Poll for unread message counts
  useEffect(() => {
    if (!student?.form || !studentId) return;

    const fetchUnreadCounts = async () => {
      try {
        const response = await fetch(buildApiUrl(`/api/student-chat/unread/${student.form}/${studentId}`));
        const data = await response.json();
        const counts = data.unread_counts || {};
        
        // Don't show group unread if group chat is currently selected or was recently opened
        const isGroupChatOpen = selectedChat === 'group';
        const groupOpenedTime = localStorage.getItem(`groupChatOpened_${student?.form}`);
        const recentlyOpened = groupOpenedTime && Date.now() - parseInt(groupOpenedTime) < 300000; // 5 minutes
        
        if (isGroupChatOpen || recentlyOpened) {
          delete counts['group'];
        }
        
        setUnreadCounts(counts);
      } catch (error) {
        // Silently handle network errors (internet disconnected, etc.)
        // The app will recover when connection is restored
      }
    };

    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 3000);

    return () => clearInterval(interval);
  }, [student?.form, studentId, selectedChat]);

  // Load reading age data for the student
  useEffect(() => {
    if (!student.full_name) return;

    // Helper function to parse different date formats
    const parseDate = (dateString) => {
      // Try parsing "19/01/2026" format
      if (dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
      }
      // Try parsing "Wed, 28 Jan 2026" format
      return new Date(dateString);
    };

    fetch(buildApiUrl('/data/reading_age.json'))
      .then(res => {
        if (!res.ok) {
          console.error(`Failed to fetch reading age: ${res.status} ${res.statusText}`);
          return Promise.reject(new Error(`HTTP ${res.status}`));
        }
        return res.json();
      })
      .then(data => {
        // Find the student's reading age tests
        const studentRecord = data.find(entry => entry.student === student.full_name);
        if (studentRecord && studentRecord.tests) {
          // Filter out invalid entries and create chart data
          const chartData = studentRecord.tests
            .filter(test => test.RA !== '-')
            .map(test => ({
              date: test.date,
              RA: parseFloat(test.RA) || 0,
              rawRA: test.RA,
              parsedDate: parseDate(test.date)
            }))
            .sort((a, b) => a.parsedDate - b.parsedDate);
          
          setReadingAgeData(chartData);
        }
      })
      .catch(err => console.error('Error loading reading age data:', err));
  }, [student.full_name]);

  // Load notes for the student
  useEffect(() => {
    if (!studentId) return;
    
    fetch(buildApiUrl(`/api/pupil-notes/${studentId}`))
      .then(res => res.json())
      .then(data => {
        console.log('Notes loaded:', data);
        if (data && data.notes && Array.isArray(data.notes)) {
          // Filter out any null/undefined items and sort
          const validNotes = data.notes.filter(note => note && note.id);
          const sorted = [...validNotes].sort((a, b) => {
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateB - dateA;
          });
          setNotes(sorted);
        } else {
          console.warn('Unexpected notes format:', data);
          setNotes([]);
        }
      })
      .catch(err => {
        console.error('Error loading notes:', err);
        setNotes([]);
      });
  }, [studentId]);

  // Load Cambridge assessment data for all modules (all years)
  useEffect(() => {
    if (!student.full_name) return;

    const loadCambridgeData = async () => {
      try {
        // Load 2025-2026 data
        const modules2025 = [
          { name: 'Mathematics', file: 'Mathematics_2025-2026.json', key: 'Mathematics (Age standardised score)', ageKey: 'Mathematics (Age equivalent score)' },
          { name: 'Arithmetic', file: 'Arthematics_2025-2026.json', key: 'Arithmetic (Age standardised score)', ageKey: 'Arithmetic (Age equivalent score)' },
          { name: 'Reading', file: 'ReadingCEM_2025-2026.json', key: 'Reading (Age standardised score)', ageKey: 'Reading (Age equivalent score)' },
          { name: 'Spelling', file: 'Spelling_2025-2026.json', key: 'Spelling (Age standardised score)', ageKey: 'Spelling (Age equivalent score)' },
          { name: 'Developed Ability', file: 'Developed_ability_2025-2026.json', key: 'Developed ability (Age standardised score)', ageKey: 'Developed ability (Age equivalent score)' }
        ];

        const assessmentData2025 = {};
        for (const module of modules2025) {
          try {
            const response = await fetch(`/${module.file}`);
            if (!response.ok) {
              console.warn(`Failed to load ${module.name} data`);
              continue;
            }
            const data = await response.json();
            const record = data.find(r => r['Full Name'] === student.full_name);
            if (record) {
              assessmentData2025[module.name] = {
                score: record[module.key],
                ageScore: record[module.ageKey],
                fullRecord: record
              };
            }
          } catch (err) {
            console.error(`Error loading ${module.name}:`, err);
          }
        }
        setCambridgeAssessmentData(assessmentData2025);

        // Load 2024-2025 data
        const modules2024 = [
          { name: 'Mathematics', file: 'Mathematics_2024-2025.json', key: 'Mathematics (Age standardised score)', ageKey: 'Mathematics (Age equivalent score)' },
          { name: 'Arithmetic', file: 'Arthematics_2024-2025.json', key: 'Arithmetic (Age standardised score)', ageKey: 'Arithmetic (Age equivalent score)' },
          { name: 'Reading', file: 'Reading_2024-2025.json', key: 'Reading (Age standardised score)', ageKey: 'Reading (Age equivalent score)' },
          { name: 'Spelling', file: 'Spelling_2024-2025.json', key: 'Spelling (Age standardised score)', ageKey: 'Spelling (Age equivalent score)' },
          { name: 'Developed Ability', file: 'Developed_ability_2024-2025.json', key: 'Developed ability (Age standardised score)', ageKey: 'Developed ability (Age equivalent score)' }
        ];

        const assessmentData2024 = {};
        for (const module of modules2024) {
          try {
            const response = await fetch(`/${module.file}`);
            if (!response.ok) {
              console.warn(`Failed to load ${module.name} 2024-2025 data`);
              continue;
            }
            const data = await response.json();
            const record = data.find(r => r['Full Name'] === student.full_name);
            if (record) {
              assessmentData2024[module.name] = {
                score: record[module.key],
                ageScore: record[module.ageKey],
                fullRecord: record
              };
            }
          } catch (err) {
            console.error(`Error loading ${module.name} 2024-2025:`, err);
          }
        }
        setCambridgeAssessmentData2024(assessmentData2024);

        // Load 2023-2024 data
        const modules2023 = [
          { name: 'Mathematics', file: 'Mathematics2023-2024.json', key: 'Mathematics (Age standardised score)', ageKey: 'Mathematics (Age equivalent score)' },
          { name: 'Arithmetic', file: 'Arthematics 2023-2024.json', key: 'Arithmetic (Age standardised score)', ageKey: 'Arithmetic (Age equivalent score)' },
          { name: 'Reading', file: 'Reading2023-2024.json', key: 'Reading (Age standardised score)', ageKey: 'Reading (Age equivalent score)' },
          { name: 'Spelling', file: 'Spelling_2023-2024.json', key: 'Spelling (Age standardised score)', ageKey: 'Spelling (Age equivalent score)' },
          { name: 'Developed Ability', file: 'Developed_ability_2023-2024.json', key: 'Developed ability (Age standardised score)', ageKey: 'Developed ability (Age equivalent score)' }
        ];

        const assessmentData2023 = {};
        for (const module of modules2023) {
          try {
            const response = await fetch(`/${module.file}`);
            if (!response.ok) {
              console.warn(`Failed to load ${module.name} 2023-2024 data`);
              continue;
            }
            const data = await response.json();
            const record = data.find(r => r['Full Name'] === student.full_name);
            if (record) {
              assessmentData2023[module.name] = {
                score: record[module.key],
                ageScore: record[module.ageKey],
                fullRecord: record
              };
            }
          } catch (err) {
            console.error(`Error loading ${module.name} 2023-2024:`, err);
          }
        }
        setCambridgeAssessmentData2023(assessmentData2023);
      } catch (err) {
        console.error('Error loading Cambridge assessment data:', err);
      }
    };

    loadCambridgeData();
  }, [student.full_name]);
  const getAvatarColor = (name) => {
    const colors = [
      'bg-gradient-to-br from-blue-400 to-blue-600',
      'bg-gradient-to-br from-green-400 to-green-600',
      'bg-gradient-to-br from-purple-400 to-purple-600',
      'bg-gradient-to-br from-pink-400 to-pink-600',
      'bg-gradient-to-br from-indigo-400 to-indigo-600',
      'bg-gradient-to-br from-teal-400 to-teal-600',
      'bg-gradient-to-br from-orange-400 to-orange-600',
      'bg-gradient-to-br from-red-400 to-red-600',
    ];
    const nameHash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[nameHash % colors.length];
  };

  // Get performance level and color for Cambridge assessment scores
  const getPerformanceLevel = (score) => {
    if (score === null || score === undefined) {
      return { level: 'No Data', color: 'bg-gray-100', textColor: 'text-gray-700', borderColor: 'border-gray-300' };
    }
    
    const numScore = parseFloat(score);
    if (numScore < 85) {
      return { 
        level: 'Well Below Average', 
        subtitle: 'Wave 3 intervention required',
        color: 'bg-red-100', 
        textColor: 'text-red-700',
        borderColor: 'border-red-300'
      };
    } else if (numScore < 93) {
      return { 
        level: 'Below Average', 
        subtitle: 'Wave 2 intervention required',
        color: 'bg-orange-100', 
        textColor: 'text-orange-700',
        borderColor: 'border-orange-300'
      };
    } else if (numScore <= 107) {
      return { 
        level: 'Average', 
        color: 'bg-yellow-100', 
        textColor: 'text-yellow-700',
        borderColor: 'border-yellow-300'
      };
    } else if (numScore <= 119) {
      return { 
        level: 'Above Average', 
        color: 'bg-blue-100', 
        textColor: 'text-blue-700',
        borderColor: 'border-blue-300'
      };
    } else if (numScore <= 129) {
      return { 
        level: 'Well Above Average', 
        subtitle: 'Upper end possible AGT provision',
        color: 'bg-indigo-100', 
        textColor: 'text-indigo-700',
        borderColor: 'border-indigo-300'
      };
    } else {
      return { 
        level: 'Gifted and Talented', 
        subtitle: 'Must have AGT provision',
        color: 'bg-purple-100', 
        textColor: 'text-purple-700',
        borderColor: 'border-purple-300'
      };
    }
  };

  // Handle teacher thumbnail click
  const handleTeacherClick = async (teacher) => {
    const teacherId = teacher.email || teacher.id;
    
    setSelectedChat(teacherId);
    setSelectedTeacher(teacher);

    // Mark messages from this teacher as read
    try {
      await fetch(buildApiUrl('/api/student-chat/mark-read'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_id: student?.form,
          user_id: studentId,
          contact_id: teacherId,
          contact_type: 'teacher'
        })
      });
      
      // Clear the unread count for this teacher
      setUnreadCounts(prev => {
        const updated = { ...prev };
        delete updated[teacherId];
        return updated;
      });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Handle class group click
  const handleGroupClick = async () => {
    setSelectedChat('group');
    setSelectedTeacher(null);

    // Store the time when group chat was opened
    localStorage.setItem(`groupChatOpened_${student?.form}`, Date.now().toString());
    
    // Clear the unread count for group immediately
    setUnreadCounts(prev => {
      const updated = { ...prev };
      delete updated['group'];
      return updated;
    });

    // Mark group messages as read
    try {
      await fetch(buildApiUrl('/api/student-chat/mark-read'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_id: student?.form,
          user_id: studentId,
          contact_id: 'group',
          contact_type: 'group'
        })
      });
    } catch (error) {
      console.error('Error marking group messages as read:', error);
    }
  };

  // Home Tab with Teams-like layout for students
  const HomeTabContent = () => {
    // Helper function to get Mr/Ms prefix and last name
    const getTeacherDisplayName = (name) => {
      if (!name) return 'Teacher';
      
      const nameParts = name.trim().split(' ');
      const firstName = nameParts[0].toLowerCase();
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
      
      const commonFemaleNames = ['sarah', 'emily', 'jennifer', 'jessica', 'lisa', 'mary', 'patricia', 'linda', 'barbara', 'susan', 'karen', 'nancy', 'betty', 'margaret', 'sandra', 'ashley', 'dorothy', 'kimberly', 'donna', 'michelle', 'carol', 'amanda', 'melissa', 'deborah', 'stephanie', 'rebecca', 'laura', 'sharon', 'cynthia', 'kathleen', 'helen', 'amy', 'anna', 'angela', 'ruth', 'brenda', 'pamela', 'nicole', 'katherine', 'samantha', 'christine', 'catherine', 'virginia', 'debra', 'rachel', 'janet', 'emma', 'carolyn', 'maria', 'heather', 'diane', 'julie', 'joyce', 'evelyn', 'joan', 'victoria', 'kelly', 'christina', 'lauren', 'frances', 'martha', 'judith', 'cheryl', 'megan', 'andrea', 'ann', 'alice', 'jean', 'doris', 'jacqueline', 'kathryn', 'hannah', 'olivia', 'gloria', 'marie', 'teresa', 'sara', 'janice', 'julia', 'grace', 'judy', 'theresa', 'rose', 'beverly', 'denise', 'marilyn', 'amber', 'danielle', 'brittany', 'diana', 'abigail', 'jane', 'natalie', 'lori', 'tiffany', 'alexis', 'kayla', 'samah'];
      
      const prefix = commonFemaleNames.includes(firstName) ? 'Ms' : 'Mr';
      return `${prefix} ${lastName}`;
    };
    
    return (
      <div className="flex h-[calc(100vh-12rem)] bg-gray-50">
        {/* Left Sidebar - Wider pane with teacher thumbnails */}
        <div className="w-32 bg-white border-r border-gray-200 flex flex-col py-4 overflow-y-auto">
          {/* Class Group Thumbnail */}
          <div className="px-3 mb-4">
            <button
              onClick={handleGroupClick}
              className={`w-full flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 ${
                selectedChat === 'group'
                  ? 'bg-blue-100 ring-2 ring-blue-500'
                  : 'hover:bg-gray-100'
              }`}
              title="Class Group Chat"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold shadow-lg mb-1 relative">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {/* Unread notification indicator */}
                {unreadCounts['group'] > 0 && (
                  <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold border-2 border-white shadow-lg animate-pulse">
                    {unreadCounts['group'] > 9 ? '9+' : unreadCounts['group']}
                  </div>
                )}
              </div>
              <span className="text-xs text-center text-gray-600 font-medium">Class</span>
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 mb-4 mx-3"></div>

          {/* Teacher Thumbnails */}
          <div className="space-y-4 px-3">
            {teachers.map((teacher) => {
              const teacherId = teacher.email || teacher.id;
              const isSelected = selectedChat === teacherId;
              const teacherName = teacher.name || teacher.full_name || teacher.email || 'Teacher';
              const displayName = getTeacherDisplayName(teacherName);
              // Use staff_id to construct photo URL
              const teacherPhoto = teacher.staff_id ? `${server}/staff-photo/${teacher.staff_id}` : null;
              
              return (
                <button
                  key={teacherId}
                  onClick={() => handleTeacherClick(teacher)}
                  className={`w-full flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 ${
                    isSelected
                      ? 'bg-blue-100 ring-2 ring-blue-500'
                      : 'hover:bg-gray-100'
                  }`}
                  title={teacherName}
                >
                  {/* Teacher Photo/Avatar */}
                  <div className="relative w-16 h-16 mb-1">
                    {teacherPhoto ? (
                      <img 
                        src={teacherPhoto}
                        alt={teacherName}
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm ${getAvatarColor(teacherName)} border-2 border-gray-200`}
                      style={{ display: teacherPhoto ? 'none' : 'flex' }}
                    >
                      {teacherName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    {/* Unread notification indicator */}
                    {unreadCounts[teacherId] > 0 && (
                      <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold border-2 border-white shadow-lg animate-pulse">
                        {unreadCounts[teacherId] > 9 ? '9+' : unreadCounts[teacherId]}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-center text-gray-600 font-medium line-clamp-1 px-1">
                    {displayName}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Pane - Chat Window */}
        <div className="flex-1 flex flex-col bg-white">
          {selectedChat ? (
            <ChatWindow
              currentUserId={studentId}
              recipientId={selectedChat === 'group' ? null : selectedChat}
              recipientName={selectedTeacher?.name || selectedTeacher?.full_name || 'Class Group'}
              isGroup={selectedChat === 'group'}
              formId={student.form}
              students={[]} // Students don't need to see other students
              chatType="student"
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <div className="text-6xl mb-4">💬</div>
              <p className="text-lg font-medium">Select a teacher or class group to start chatting</p>
              <p className="text-sm mt-2">Your teachers and class group are in the sidebar</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Back Button */}
      <button
        onClick={() => window.history.back()}
        className="mb-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium flex items-center gap-2"
        title="Back to Form Page"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Form
      </button>
      
      {/* Header with Student Photo */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center gap-6">
          {/* Student Photo */}
          <div className="flex-shrink-0">
            <img
              src={student.school_id ? `${server}/student-photo/${student.school_id}?v=3` : '/default-avatar.png'}
              alt={student.full_name || 'Student'}
              className="w-[200px] h-[150px] object-cover rounded-lg border-4 border-gray-200 shadow-md"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="150" viewBox="0 0 300 150"%3E%3Crect fill="%23e5e7eb" width="300" height="150"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="48" fill="%239ca3af"%3E' + (student.full_name?.charAt(0) || '?') + '%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>
          {/* Student Info */}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{student.full_name || "Loading..."}</h1>
            <div className="flex flex-wrap gap-3">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                🆔 {student.school_id || "N/A"}
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                🎓 Form {student.form || "N/A"}
              </span>
              {student.academic_house && (
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                  🏠 {student.academic_house}
                </span>
              )}
            </div>
          </div>
          {/* Attendance Score & Rewards Points - Far Right */}
          <div className="flex-shrink-0 flex items-stretch gap-3">
            {/* Attendance Score */}
            <div className={`rounded-lg px-5 py-4 shadow-lg text-center ${attendanceScore?.attendance_percentage >= 95 ? 'bg-gradient-to-br from-green-400 to-emerald-600' : attendanceScore?.attendance_percentage >= 90 ? 'bg-gradient-to-br from-blue-400 to-blue-600' : attendanceScore?.attendance_percentage >= 80 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' : 'bg-gradient-to-br from-red-400 to-red-600'}`}>
              <div className="text-white text-sm font-semibold mb-1">
                📊 Attendance
              </div>
              <div className="text-white text-3xl font-bold">
                {attendanceScore ? `${attendanceScore.attendance_percentage}%` : 'N/A'}
              </div>
              {attendanceScore && (
                <div className="text-white/80 text-xs mt-1">
                  {attendanceScore.present_days + attendanceScore.late_days}/{attendanceScore.total_days} days
                </div>
              )}
            </div>
            {/* Rewards Points */}
            <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg px-6 py-4 shadow-lg text-center">
              <div className="text-white text-sm font-semibold mb-1">
                {rewardsData.reward_level?.icon || '🏆'} Reward Points
              </div>
              <div className="text-white text-3xl font-bold">
                {rewardsData.total_points}
              </div>
              {rewardsData.reward_level && (
                <div 
                  className="text-xs font-semibold mt-1 px-2 py-1 rounded-full inline-block"
                  style={{ 
                    backgroundColor: rewardsData.reward_level.color,
                    color: rewardsData.reward_level.name === 'Silver' || rewardsData.reward_level.name === 'Platinum' ? '#000' : '#fff'
                  }}
                >
                  {rewardsData.reward_level.name}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex border-b mb-4 bg-white rounded-t-lg shadow">
        <button
          className={`px-4 py-2 font-medium ${tab === "core-data" ? "border-b-2 border-blue-600 text-blue-700" : "text-gray-500"}`}
          onClick={() => setTab("core-data")}
        >
          📋 Core Data
        </button>
        <button
          className={`px-4 py-2 font-medium ${tab === "academic-level" ? "border-b-2 border-blue-600 text-blue-700" : "text-gray-500"}`}
          onClick={() => setTab("academic-level")}
        >
          📚 Academic Level
        </button>
        <button
          className={`px-4 py-2 font-medium ${tab === "academic-progress" ? "border-b-2 border-blue-600 text-blue-700" : "text-gray-500"}`}
          onClick={() => setTab("academic-progress")}
        >
          📈 Academic Progress
        </button>
        <button
          className={`px-4 py-2 font-medium ${tab === "history" ? "border-b-2 border-blue-600 text-blue-700" : "text-gray-500"}`}
          onClick={() => setTab("history")}
        >
          📖 History
        </button>
        <button
          className={`px-4 py-2 font-medium ${tab === "medical-history" ? "border-b-2 border-blue-600 text-blue-700" : "text-gray-500"}`}
          onClick={() => setTab("medical-history")}
        >
          🏥 Medical History
        </button>
        <button
          className={`px-4 py-2 font-medium ${tab === "notes" ? "border-b-2 border-blue-600 text-blue-700" : "text-gray-500"}`}
          onClick={() => setTab("notes")}
        >
          📝 Notes
        </button>
        <button
          className={`px-4 py-2 font-medium ${tab === "documents" ? "border-b-2 border-blue-600 text-blue-700" : "text-gray-500"}`}
          onClick={() => setTab("documents")}
        >
          📁 Documents
        </button>
      </div>

      {tab === "core-data" && (
        <div className="mt-4 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🆔 School ID:</span>
                <span className="font-semibold">{student.school_id || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🔑 Pupil ID:</span>
                <span className="font-semibold">{student.pupil_id || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">👤 Person ID:</span>
                <span className="font-semibold">{student.person_id || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">📛 Full Name:</span>
                <span className="font-semibold">{student.full_name || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">
                  {student.gender === "Male" ? "👦" : student.gender === "Female" ? "👧" : "👤"} Gender:
                </span>
                <span className="font-semibold">{student.gender || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🎂 Date of Birth:</span>
                <span className="font-semibold">
                  {student.dob ? new Date(student.dob).toLocaleDateString() : "N/A"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">�🌍 Nationality:</span>
                <span className="font-semibold">{student.nationality || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">📧 Email:</span>
                <span className="font-semibold text-sm">{student.email || "N/A"}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Academic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🏫 Form/Class:</span>
                <span className="font-semibold">{student.form || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">📊 Year Group:</span>
                <span className="font-semibold">{student.nc_year ? getYearLabel(student.nc_year) : "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🏠 Academic House:</span>
                <span className="font-semibold">{student.academic_house || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🏢 Division:</span>
                <span className="font-semibold">{student.division_name || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🔢 Division ID:</span>
                <span className="font-semibold">{student.division_id || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🏫 School:</span>
                <span className="font-semibold">{student.school || "Main School"}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Enrolment Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">📅 Enrolment Date:</span>
                <span className="font-semibold">
                  {student.enrolment_date ? new Date(student.enrolment_date).toLocaleDateString() : "N/A"}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">📱 Phone:</span>
                <span className="font-semibold">{student.phone || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🏡 Address:</span>
                <span className="font-semibold">{student.address || "N/A"}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Parent/Guardian Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">👨‍👩‍👧 Guardian:</span>
                <span className="font-semibold">{student.guardian_name || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">📞 Guardian Phone:</span>
                <span className="font-semibold">{student.guardian_phone || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3 md:col-span-2">
                <span className="text-gray-600 font-medium w-40">📧 Guardian Email:</span>
                <span className="font-semibold">{student.guardian_email || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "academic-level" && (
        <div className="mt-4 space-y-6">
          {/* Cambridge Insight Section with Chart - TOP */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4 border-b pb-2">
              <h3 className="text-lg font-bold text-gray-900">📊 Cambridge Insight - Assessment Overview</h3>
              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedAcademicYear("2023-2024")}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      selectedAcademicYear === "2023-2024"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    2023-24
                  </button>
                  <button
                    onClick={() => setSelectedAcademicYear("2024-2025")}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      selectedAcademicYear === "2024-2025"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    2024-25
                  </button>
                  <button
                    onClick={() => setSelectedAcademicYear("2025-2026")}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      selectedAcademicYear === "2025-2026"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    2025-26
                  </button>
                </div>
                {selectedAcademicYear === "2025-2026" && (
                  <span className="text-gray-500 text-sm">Sep 2025</span>
                )}
              </div>
              {selectedAcademicYear === "2025-2026" && cambridgeAssessmentData?.['Developed Ability']?.fullRecord?.['Developed ability (Age at assessment)'] && (
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">Age at Assessment:</span> {cambridgeAssessmentData['Developed Ability'].fullRecord['Developed ability (Age at assessment)'].toFixed(2)} years
                </div>
              )}
              {selectedAcademicYear === "2024-2025" && cambridgeAssessmentData2024?.['Developed Ability']?.fullRecord?.['Developed ability (Age at assessment)'] && (
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">Age at Assessment:</span> {cambridgeAssessmentData2024['Developed Ability'].fullRecord['Developed ability (Age at assessment)'].toFixed(2)} years
                </div>
              )}
              {selectedAcademicYear === "2023-2024" && cambridgeAssessmentData2023?.['Developed Ability']?.fullRecord?.['Developed ability (Age at assessment)'] && (
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">Age at Assessment:</span> {cambridgeAssessmentData2023['Developed Ability'].fullRecord['Developed ability (Age at assessment)'].toFixed(2)} years
                </div>
              )}
            </div>
            
            {(selectedAcademicYear === "2025-2026" ? cambridgeAssessmentData : selectedAcademicYear === "2024-2025" ? cambridgeAssessmentData2024 : cambridgeAssessmentData2023) && Object.keys(selectedAcademicYear === "2025-2026" ? cambridgeAssessmentData : selectedAcademicYear === "2024-2025" ? cambridgeAssessmentData2024 : cambridgeAssessmentData2023).length > 0 ? (
              <div className="space-y-6">
                <p className="text-sm text-gray-600">Assessment data comparison across all major Cambridge modules</p>
                
                {/* Two Side-by-Side Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Chart: Standardised Scores */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Standardised Scores</h4>
                    <div className="w-full h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <ScatterChart margin={{ top: 20, right: 20, left: 20, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            type="category" 
                            dataKey="name" 
                            name="Assessment Module"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fill: '#6b7280', fontSize: 11 }}
                          />
                          <YAxis 
                            type="number" 
                            dataKey="score" 
                            name="Age-standardised Score"
                            domain={[40, 160]}
                            tick={{ fill: '#6b7280', fontSize: 11 }}
                          />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 border-2 border-gray-300 rounded shadow-lg">
                                    <p className="font-semibold text-gray-900">{data.name}</p>
                                    <p className="text-gray-700">Score: {data.score || 'N/A'}</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <ReferenceLine y={100} stroke="#3b82f6" strokeDasharray="5 5" label={{ value: '100', position: 'right', fill: '#1e40af', fontSize: 11 }} />
                          <Scatter 
                            data={['Mathematics', 'Arithmetic', 'Reading', 'Spelling', 'Developed Ability'].map(moduleName => {
                              const assessmentData = selectedAcademicYear === "2025-2026" ? cambridgeAssessmentData : selectedAcademicYear === "2024-2025" ? cambridgeAssessmentData2024 : cambridgeAssessmentData2023;
                              const moduleData = assessmentData[moduleName];
                              const score = moduleData?.score;
                              const numScore = score !== null && score !== undefined ? parseFloat(score) : null;
                              
                              let color = '#9ca3af';
                              if (numScore !== null) {
                                if (numScore < 85) color = '#dc2626';
                                else if (numScore < 93) color = '#ea580c';
                                else if (numScore <= 107) color = '#22c55e';
                                else if (numScore <= 119) color = '#60a5fa';
                                else if (numScore <= 129) color = '#2563eb';
                                else color = '#f97316';
                              }
                              
                              return {
                                name: moduleName,
                                score: numScore || 0,
                                color: color,
                                actualScore: numScore
                              };
                            })}
                            fill="#8884d8"
                            shape="circle"
                          >
                            {['Mathematics', 'Arithmetic', 'Reading', 'Spelling', 'Developed Ability'].map((moduleName, index) => {
                              const assessmentData = selectedAcademicYear === "2025-2026" ? cambridgeAssessmentData : selectedAcademicYear === "2024-2025" ? cambridgeAssessmentData2024 : cambridgeAssessmentData2023;
                              const moduleData = assessmentData[moduleName];
                              const score = moduleData?.score;
                              const numScore = score !== null && score !== undefined ? parseFloat(score) : null;
                              
                              let color = '#9ca3af';
                              if (numScore !== null) {
                                if (numScore < 85) color = '#dc2626';
                                else if (numScore < 93) color = '#ea580c';
                                else if (numScore <= 107) color = '#22c55e';
                                else if (numScore <= 119) color = '#60a5fa';
                                else if (numScore <= 129) color = '#2563eb';
                                else color = '#f97316';
                              }
                              
                              return <Cell key={`cell-${index}`} fill={color} />;
                            })}
                          </Scatter>
                        </ScatterChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Right Chart: Age Equivalent Scores */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Age Equivalent Scores</h4>
                    <div className="w-full h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <ScatterChart margin={{ top: 20, right: 20, left: 20, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            type="category" 
                            dataKey="name" 
                            name="Assessment Module"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fill: '#6b7280', fontSize: 11 }}
                          />
                          <YAxis 
                            type="number" 
                            dataKey="ageScore" 
                            name="Age Equivalent Score"
                            domain={[0, 20]}
                            tick={{ fill: '#6b7280', fontSize: 11 }}
                          />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 border-2 border-gray-300 rounded shadow-lg">
                                    <p className="font-semibold text-gray-900">{data.name}</p>
                                    <p className="text-gray-700">Age Equivalent: {data.ageScore || 'N/A'}</p>
                                    <p className="text-gray-700">Age Difference: {data.ageDifference !== null && data.ageDifference !== undefined ? (data.ageDifference > 0 ? '+' : '') + data.ageDifference.toFixed(2) : 'N/A'}</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <ReferenceLine 
                            y={(() => {
                              const assessmentData = selectedAcademicYear === "2025-2026" ? cambridgeAssessmentData : selectedAcademicYear === "2024-2025" ? cambridgeAssessmentData2024 : cambridgeAssessmentData2023;
                              const firstModule = ['Mathematics', 'Arithmetic', 'Reading', 'Spelling', 'Developed Ability'].find(m => assessmentData[m]?.fullRecord);
                              if (firstModule) {
                                const ageAtAssessment = assessmentData[firstModule]?.fullRecord?.[`${firstModule} (Age at assessment)`];
                                return ageAtAssessment ? parseFloat(ageAtAssessment) : 10;
                              }
                              return 10;
                            })()} 
                            stroke="#3b82f6" 
                            strokeDasharray="5 5" 
                            label={{
                              value: (() => {
                                const assessmentData = selectedAcademicYear === "2025-2026" ? cambridgeAssessmentData : selectedAcademicYear === "2024-2025" ? cambridgeAssessmentData2024 : cambridgeAssessmentData2023;
                                const firstModule = ['Mathematics', 'Arithmetic', 'Reading', 'Spelling', 'Developed Ability'].find(m => assessmentData[m]?.fullRecord);
                                if (firstModule) {
                                  const ageAtAssessment = assessmentData[firstModule]?.fullRecord?.[`${firstModule} (Age at assessment)`];
                                  if (ageAtAssessment) {
                                    const years = Math.floor(ageAtAssessment);
                                    const months = Math.round((ageAtAssessment % 1) * 12);
                                    return `${years}y ${months}m`;
                                  }
                                }
                                return '10';
                              })(),
                              position: 'right',
                              fill: '#1e40af',
                              fontSize: 11
                            }}
                          />
                          <Scatter 
                            data={['Mathematics', 'Arithmetic', 'Reading', 'Spelling', 'Developed Ability'].map(moduleName => {
                              const assessmentData = selectedAcademicYear === "2025-2026" ? cambridgeAssessmentData : selectedAcademicYear === "2024-2025" ? cambridgeAssessmentData2024 : cambridgeAssessmentData2023;
                              const moduleData = assessmentData[moduleName];
                              const ageScore = moduleData?.ageScore;
                              const numAgeScore = ageScore !== null && ageScore !== undefined ? parseFloat(ageScore) : null;
                              const ageDifference = moduleData?.fullRecord?.[`${moduleName} (Age difference)`];
                              const numAgeDifference = ageDifference !== null && ageDifference !== undefined ? parseFloat(ageDifference) : null;
                              
                              let color = '#9ca3af';
                              if (numAgeScore !== null) {
                                if (numAgeScore < 8) color = '#dc2626';
                                else if (numAgeScore < 9) color = '#ea580c';
                                else if (numAgeScore <= 11) color = '#22c55e';
                                else if (numAgeScore <= 12) color = '#60a5fa';
                                else if (numAgeScore <= 13) color = '#2563eb';
                                else color = '#f97316';
                              }
                              
                              return {
                                name: moduleName,
                                ageScore: numAgeScore || 0,
                                ageDifference: numAgeDifference,
                                color: color,
                                actualAgeScore: numAgeScore
                              };
                            })}
                            fill="#8884d8"
                            shape="circle"
                          >
                            {['Mathematics', 'Arithmetic', 'Reading', 'Spelling', 'Developed Ability'].map((moduleName, index) => {
                              const assessmentData = selectedAcademicYear === "2025-2026" ? cambridgeAssessmentData : selectedAcademicYear === "2024-2025" ? cambridgeAssessmentData2024 : cambridgeAssessmentData2023;
                              const moduleData = assessmentData[moduleName];
                              const ageScore = moduleData?.ageScore;
                              const numAgeScore = ageScore !== null && ageScore !== undefined ? parseFloat(ageScore) : null;
                              
                              let color = '#9ca3af';
                              if (numAgeScore !== null) {
                                if (numAgeScore < 8) color = '#dc2626';
                                else if (numAgeScore < 9) color = '#ea580c';
                                else if (numAgeScore <= 11) color = '#22c55e';
                                else if (numAgeScore <= 12) color = '#60a5fa';
                                else if (numAgeScore <= 13) color = '#2563eb';
                                else color = '#f97316';
                              }
                              
                              return <Cell key={`cell-age-${index}`} fill={color} />;
                            })}
                          </Scatter>
                        </ScatterChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Individual Score Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mt-6">
                  {['Mathematics', 'Arithmetic', 'Reading', 'Spelling', 'Developed Ability'].map((moduleName) => {
                    const assessmentData = selectedAcademicYear === "2025-2026" ? cambridgeAssessmentData : selectedAcademicYear === "2024-2025" ? cambridgeAssessmentData2024 : cambridgeAssessmentData2023;
                    const moduleData = assessmentData[moduleName];
                    const score = moduleData?.score;
                    const performance = getPerformanceLevel(score);
                    const linkedTarget = targets.find(t => t.assessment_module === moduleName && t.status === 'active');
                    
                    return (
                      <div 
                        key={moduleName}
                        className={`${performance.color} border-2 ${performance.borderColor} rounded-lg p-4 transition-all hover:shadow-md cursor-pointer group`}
                        onClick={() => openTargetModal(moduleName, score)}
                        title="Click to set or link a target"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-semibold text-gray-700">{moduleName}</div>
                          <span className="text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity font-medium">🎯 Target</span>
                        </div>
                        
                        {score !== null && score !== undefined ? (
                          <>
                            <div className={`text-3xl font-bold ${performance.textColor} mb-2`}>
                              {typeof score === 'number' ? score.toFixed(0) : score}
                            </div>
                            <div className={`text-xs font-semibold ${performance.textColor}`}>
                              {performance.level}
                            </div>
                            {performance.subtitle && (
                              <div className="text-xs text-gray-600 mt-1 leading-tight">
                                {performance.subtitle}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-gray-600 text-sm italic">No data available</div>
                        )}
                        {linkedTarget && (
                          <div className="mt-2 text-xs bg-white bg-opacity-70 rounded px-2 py-1 text-blue-700 font-medium truncate">
                            🎯 {linkedTarget.title}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Color Legend */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">Performance Level Guide</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-600 rounded"></div>
                      <span className="text-gray-700">&lt;85: Well Below Average (Wave 3)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-orange-600 rounded"></div>
                      <span className="text-gray-700">85-92: Below Average (Wave 2)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-600 rounded"></div>
                      <span className="text-gray-700">93-107: Average</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-400 rounded"></div>
                      <span className="text-gray-700">108-119: Above Average</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-700 rounded"></div>
                      <span className="text-gray-700">120-129: Well Above Average (AGT)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-orange-500 rounded"></div>
                      <span className="text-gray-700">≥130: Gifted & Talented (AGT)</span>
                    </div>
                  </div>
                </div>

                {/* Expandable Detailed Assessment Table */}
                <div className="mt-8 bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 w-40">Assessment Module</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Age at Assessment</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Age-Equivalent Score</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Age Difference</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Age-Standardised Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['Mathematics', 'Arithmetic', 'Reading', 'Spelling', 'Developed Ability'].map((moduleName) => {
                        const assessmentData = selectedAcademicYear === "2025-2026" ? cambridgeAssessmentData : selectedAcademicYear === "2024-2025" ? cambridgeAssessmentData2024 : cambridgeAssessmentData2023;
                        const moduleData = assessmentData[moduleName]?.fullRecord;
                        const isExpanded = expandedModules[moduleName] || false;
                        
                        // Define sub-components for each module
                        const subModules = {
                          'Mathematics': ['Numbers 1', 'Numbers 2', 'Measures, shape and space', 'Data handling'],
                          'Arithmetic': ['Addition', 'Subtraction', 'Multiplication', 'Division'],
                          'Reading': ['Word recognition', 'Word decoding', 'Comprehension'],
                          'Spelling': [],
                          'Developed Ability': ['Picture vocabulary', 'Non-verbal']
                        };
                        
                        const subList = subModules[moduleName] || [];
                        
                        return (
                          <React.Fragment key={moduleName}>
                            {/* Main Module Row */}
                            <tr className="border-b border-gray-200 hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => setExpandedModules(prev => ({...prev, [moduleName]: !prev[moduleName]}))}
                                  className="flex items-center gap-2 text-gray-900 font-semibold hover:text-blue-600"
                                >
                                  <span className="inline-block w-5 h-5 text-center">
                                    {subList.length > 0 ? (isExpanded ? '▼' : '▶') : '•'}
                                  </span>
                                  {moduleName} (module)
                                </button>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {moduleData?.[`${moduleName} (Age at assessment)`] ? 
                                  `${Math.floor(moduleData[`${moduleName} (Age at assessment)`])}y ${Math.round((moduleData[`${moduleName} (Age at assessment)`] % 1) * 12)}m` 
                                  : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {moduleData?.[`${moduleName} (Age equivalent score)`] ? 
                                  `${Math.floor(moduleData[`${moduleName} (Age equivalent score)`])}y ${Math.round((moduleData[`${moduleName} (Age equivalent score)`] % 1) * 12)}m` 
                                  : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {moduleData?.[`${moduleName} (Age difference)`] !== undefined && moduleData[`${moduleName} (Age difference)`] !== null ?
                                  moduleData[`${moduleName} (Age difference)`] >= 0 ?
                                    `+${moduleData[`${moduleName} (Age difference)`].toFixed(1)}` :
                                    moduleData[`${moduleName} (Age difference)`].toFixed(1)
                                  : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                {moduleData?.[`${moduleName} (Age standardised score)`] || '—'}
                              </td>
                            </tr>
                            
                            {/* Sub-Module Rows */}
                            {isExpanded && subList.map((subModule) => (
                              <tr key={`${moduleName}-${subModule}`} className="border-b border-gray-200 bg-blue-50 hover:bg-blue-100">
                                <td className="px-4 py-2 pl-12 text-sm text-blue-900">{subModule}</td>
                                <td className="px-4 py-2 text-sm text-gray-700">
                                  {moduleData?.[`${subModule} (Age at assessment)`] ? 
                                    `${Math.floor(moduleData[`${subModule} (Age at assessment)`])}y ${Math.round((moduleData[`${subModule} (Age at assessment)`] % 1) * 12)}m` 
                                    : '—'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700">
                                  {moduleData?.[`${subModule} (Age equivalent score)`] ? 
                                    `${Math.floor(moduleData[`${subModule} (Age equivalent score)`])}y ${Math.round((moduleData[`${subModule} (Age equivalent score)`] % 1) * 12)}m` 
                                    : '—'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700">
                                  {moduleData?.[`${subModule} (Age difference)`] !== undefined && moduleData[`${subModule} (Age difference)`] !== null ?
                                    moduleData[`${subModule} (Age difference)`] >= 0 ?
                                      `+${moduleData[`${subModule} (Age difference)`].toFixed(1)}` :
                                      moduleData[`${subModule} (Age difference)`].toFixed(1)
                                    : '—'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-700 text-center">•</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📊</div>
                <p>Cambridge assessment data not yet available for this student.</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Current Academic Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🎓 Current Form:</span>
                <span className="font-semibold text-lg">{student.form || "N/A"}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">📊 Academic Year:</span>
                <span className="font-semibold">{new Date().getFullYear()}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-40">🏠 Academic House:</span>
                <span className="font-semibold">{student.academic_house || "N/A"}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Assignments & Progress</h3>
            {studentAssignments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📝</div>
                <p>No assignments found.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {studentAssignments.map((hw, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{hw.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {hw.subject} — Due: {hw.dueDate}
                        </p>
                      </div>
                      <button
                        className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        onClick={() => window.location.href = `/student-homework-viewer/${student.school_id}/${hw.id}`}
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Performance Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">{studentAssignments.length}</div>
                <div className="text-sm text-gray-600 mt-1">Total Assignments</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">-</div>
                <div className="text-sm text-gray-600 mt-1">Completed</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-3xl font-bold text-yellow-600">-</div>
                <div className="text-sm text-gray-600 mt-1">Pending</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="mt-4 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Academic History</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">📅</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Current Academic Year</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Form: {student.form || "N/A"} | Year: {new Date().getFullYear()}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Student is currently enrolled in form {student.form} for the academic year {new Date().getFullYear()}.
                  </p>
                </div>
              </div>
              
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📚</div>
                <p>Historical records will appear here</p>
                <p className="text-sm mt-2">Past academic years, achievements, and milestones</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Attendance History</h3>
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">✅</div>
              <p>Attendance records will appear here</p>
              <p className="text-sm mt-2">Track of presence, absences, and punctuality</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Behavioral Records</h3>
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">⭐</div>
              <p>Behavioral history will appear here</p>
              <p className="text-sm mt-2">Achievements, awards, and notable events</p>
            </div>
          </div>
        </div>
      )}

      {tab === "medical-history" && (
        <div className="mt-4 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Medical Consent & Conditions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-48">📋 Medical Consent Form:</span>
                <span className={`font-semibold px-3 py-1 rounded-full text-sm ${
                  medicalFields.consentFormSigned 
                    ? "bg-green-100 text-green-800" 
                    : "bg-red-100 text-red-800"
                }`}>
                  {medicalFields.consentFormSigned ? "✓ Signed" : "✗ Not Signed"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium w-48">🏥 Has Medical Condition:</span>
                <span className={`font-semibold px-3 py-1 rounded-full text-sm ${
                  medicalFields.hasMedicalCondition 
                    ? "bg-yellow-100 text-yellow-800" 
                    : "bg-gray-100 text-gray-800"
                }`}>
                  {medicalFields.hasMedicalCondition ? "Yes" : "No"}
                </span>
              </div>
              {medicalFields.hasMedicalCondition && medicalFields.medicalConditionNotes && (
                <div className="md:col-span-2">
                  <div className="flex flex-col gap-2">
                    <span className="text-gray-600 font-medium">📝 Medical Condition Notes:</span>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-gray-800">{medicalFields.medicalConditionNotes}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">Growth Tracking</h3>
            {bmiData.length > 0 ? (
              <div className="space-y-8">
                {/* Height Chart */}
                <div>
                  <h4 className="text-md font-semibold text-gray-800 mb-4">📏 Height Over Time (cm)</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={bmiData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis 
                        style={{ fontSize: '12px' }}
                        label={{ value: 'Height (cm)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                        formatter={(value) => [`${value} cm`, 'Height']}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="height" 
                        stroke="#3b82f6" 
                        strokeWidth={3}
                        dot={{ fill: '#3b82f6', r: 5 }}
                        activeDot={{ r: 7 }}
                        name="Height (cm)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Weight Chart */}
                <div>
                  <h4 className="text-md font-semibold text-gray-800 mb-4">⚖️ Weight Over Time (kg)</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={bmiData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis 
                        style={{ fontSize: '12px' }}
                        label={{ value: 'Weight (kg)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                        formatter={(value) => [`${value} kg`, 'Weight']}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="weight" 
                        stroke="#10b981" 
                        strokeWidth={3}
                        dot={{ fill: '#10b981', r: 5 }}
                        activeDot={{ r: 7 }}
                        name="Weight (kg)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {bmiData[bmiData.length - 1]?.height || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Latest Height (cm)</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {bmiData[bmiData.length - 1]?.weight || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Latest Weight (kg)</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {bmiData.length}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Total Measurements</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {bmiData[bmiData.length - 1]?.date || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Last Recorded</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📊</div>
                <p>No growth tracking data available</p>
                <p className="text-sm mt-2">Height and weight measurements will appear here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "academic-progress" && (
        <div className="mt-4 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">📈 Academic Progress - Year-on-Year Comparison (2023-24 to 2025-26)</h3>
            
            {/* Module Selection Tabs */}
            <div className="flex gap-2 mb-6 flex-wrap">
              <button
                onClick={() => setSelectedModule("AR Reading Age")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedModule === "AR Reading Age"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                📖 AR Reading Age
              </button>
              {['Mathematics', 'Arithmetic', 'Reading', 'Spelling', 'Developed Ability'].map(module => (
                <button
                  key={module}
                  onClick={() => setSelectedModule(module)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedModule === module
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {module}
                </button>
              ))}
            </div>

            {/* AR Reading Age Section */}
            {selectedModule === "AR Reading Age" && (
              <div className="space-y-6">
                {readingAgeData.length > 0 ? (
                  <>
                    <div className="w-full h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={readingAgeData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            angle={-45}
                            textAnchor="end"
                            height={100}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis 
                            label={{ value: 'Reading Age', angle: -90, position: 'insideLeft' }}
                            domain={['dataMin - 1', 'dataMax + 1']}
                          />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                return (
                                  <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                                    <p className="font-semibold text-gray-900">{payload[0].payload.date}</p>
                                    <p className="text-blue-600">RA: {payload[0].payload.rawRA}</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="RA" 
                            stroke="#2563eb" 
                            strokeWidth={2}
                            dot={{ fill: '#2563eb', r: 5 }}
                            activeDot={{ r: 7 }}
                            label={{ position: 'top', fill: '#2563eb', fontSize: 12, fontWeight: 'bold', offset: 10 }}
                            name="Reading Age"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Total Tests:</span> {readingAgeData.length} | 
                        <span className="font-semibold ml-4">Latest RA:</span> {readingAgeData.length > 0 ? readingAgeData[readingAgeData.length - 1].rawRA : 'N/A'} |
                        <span className="font-semibold ml-4">Lowest:</span> {readingAgeData.length > 0 ? Math.min(...readingAgeData.map(d => parseFloat(d.RA) || 0)).toFixed(2) : 'N/A'} |
                        <span className="font-semibold ml-4">Highest:</span> {readingAgeData.length > 0 ? Math.max(...readingAgeData.map(d => parseFloat(d.RA) || 0)).toFixed(2) : 'N/A'}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📚</div>
                    <p>No Reading Age data available</p>
                  </div>
                )}
              </div>
            )}

            {/* Progress Chart */}
            {(selectedModule === 'Mathematics' || selectedModule === 'Arithmetic' || selectedModule === 'Reading' || selectedModule === 'Spelling' || selectedModule === 'Developed Ability') && (
              <div className="space-y-6">
                {/* Standardised Scores Progress */}
                <div>
                  <h4 className="text-md font-semibold text-gray-800 mb-4">Age Standardised Scores Progress</h4>
                  <div className="w-full h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart 
                        data={[
                          {
                            year: "2023-24",
                            score: cambridgeAssessmentData2023?.[selectedModule]?.score || null,
                            fullRecord: cambridgeAssessmentData2023?.[selectedModule]?.fullRecord
                          },
                          {
                            year: "2024-25",
                            score: cambridgeAssessmentData2024?.[selectedModule]?.score || null,
                            fullRecord: cambridgeAssessmentData2024?.[selectedModule]?.fullRecord
                          },
                          {
                            year: "2025-26",
                            score: cambridgeAssessmentData?.[selectedModule]?.score || null,
                            fullRecord: cambridgeAssessmentData?.[selectedModule]?.fullRecord
                          }
                        ]}
                        margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="year" 
                          tick={{ fill: '#6b7280', fontSize: 12 }}
                        />
                        <YAxis 
                          domain={[40, 160]}
                          tick={{ fill: '#6b7280', fontSize: 12 }}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '2px solid #d1d5db', borderRadius: '8px' }}
                          formatter={(value) => value !== null ? value.toFixed(0) : 'N/A'}
                          labelStyle={{ color: '#000' }}
                        />
                        <Legend />
                        <ReferenceLine y={100} stroke="#9ca3af" strokeDasharray="5 5" label={{ value: 'Average (100)', position: 'right', fill: '#6b7280', fontSize: 11 }} />
                        <Line 
                          type="monotone" 
                          dataKey="score" 
                          stroke="#2563eb" 
                          strokeWidth={3}
                          dot={{ fill: '#2563eb', r: 6 }}
                          activeDot={{ r: 8 }}
                          connectNulls
                          name="Standardised Score"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                      <div className="text-sm text-gray-600">2023-24</div>
                      <div className="text-2xl font-bold text-blue-600 mt-1">
                        {cambridgeAssessmentData2023?.[selectedModule]?.score ? cambridgeAssessmentData2023[selectedModule].score.toFixed(0) : '—'}
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-200">
                      <div className="text-sm text-gray-600">2024-25</div>
                      <div className="text-2xl font-bold text-purple-600 mt-1">
                        {cambridgeAssessmentData2024?.[selectedModule]?.score ? cambridgeAssessmentData2024[selectedModule].score.toFixed(0) : '—'}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                      <div className="text-sm text-gray-600">2025-26</div>
                      <div className="text-2xl font-bold text-green-600 mt-1">
                        {cambridgeAssessmentData?.[selectedModule]?.score ? cambridgeAssessmentData[selectedModule].score.toFixed(0) : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Age Equivalent Scores Progress */}
                <div>
                  <h4 className="text-md font-semibold text-gray-800 mb-4">Age Equivalent Scores Progress</h4>
                  <div className="w-full h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart 
                        data={[
                          {
                            year: "2023-24",
                            ageScore: cambridgeAssessmentData2023?.[selectedModule]?.ageScore || null
                          },
                          {
                            year: "2024-25",
                            ageScore: cambridgeAssessmentData2024?.[selectedModule]?.ageScore || null
                          },
                          {
                            year: "2025-26",
                            ageScore: cambridgeAssessmentData?.[selectedModule]?.ageScore || null
                          }
                        ]}
                        margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="year" 
                          tick={{ fill: '#6b7280', fontSize: 12 }}
                        />
                        <YAxis 
                          domain={[0, 20]}
                          tick={{ fill: '#6b7280', fontSize: 12 }}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '2px solid #d1d5db', borderRadius: '8px' }}
                          formatter={(value) => value !== null ? value.toFixed(1) : 'N/A'}
                          labelStyle={{ color: '#000' }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="ageScore" 
                          stroke="#ea580c" 
                          strokeWidth={3}
                          dot={{ fill: '#ea580c', r: 6 }}
                          activeDot={{ r: 8 }}
                          connectNulls
                          name="Age Equivalent (Years)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="bg-orange-50 rounded-lg p-4 text-center border border-orange-200">
                      <div className="text-sm text-gray-600">2023-24</div>
                      <div className="text-2xl font-bold text-orange-600 mt-1">
                        {cambridgeAssessmentData2023?.[selectedModule]?.ageScore ? cambridgeAssessmentData2023[selectedModule].ageScore.toFixed(1) : '—'}
                      </div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 text-center border border-yellow-200">
                      <div className="text-sm text-gray-600">2024-25</div>
                      <div className="text-2xl font-bold text-yellow-600 mt-1">
                        {cambridgeAssessmentData2024?.[selectedModule]?.ageScore ? cambridgeAssessmentData2024[selectedModule].ageScore.toFixed(1) : '—'}
                      </div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 text-center border border-red-200">
                      <div className="text-sm text-gray-600">2025-26</div>
                      <div className="text-2xl font-bold text-red-600 mt-1">
                        {cambridgeAssessmentData?.[selectedModule]?.ageScore ? cambridgeAssessmentData[selectedModule].ageScore.toFixed(1) : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Age Difference Progress */}
                <div>
                  <h4 className="text-md font-semibold text-gray-800 mb-4">Age Difference Progress</h4>
                  <div className="w-full h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart 
                        data={[
                          {
                            year: "2023-24",
                            ageDifference: cambridgeAssessmentData2023?.[selectedModule]?.fullRecord?.[`${selectedModule} (Age difference)`] || null
                          },
                          {
                            year: "2024-25",
                            ageDifference: cambridgeAssessmentData2024?.[selectedModule]?.fullRecord?.[`${selectedModule} (Age difference)`] || null
                          },
                          {
                            year: "2025-26",
                            ageDifference: cambridgeAssessmentData?.[selectedModule]?.fullRecord?.[`${selectedModule} (Age difference)`] || null
                          }
                        ]}
                        margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="year" 
                          tick={{ fill: '#6b7280', fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fill: '#6b7280', fontSize: 12 }}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#fff', border: '2px solid #d1d5db', borderRadius: '8px' }}
                          formatter={(value) => value !== null ? (value > 0 ? '+' : '') + value.toFixed(2) : 'N/A'}
                          labelStyle={{ color: '#000' }}
                        />
                        <Legend />
                        <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="5 5" label={{ value: '0 (At age level)', position: 'right', fill: '#6b7280', fontSize: 11 }} />
                        <Line 
                          type="monotone" 
                          dataKey="ageDifference" 
                          stroke="#8b5cf6" 
                          strokeWidth={3}
                          dot={{ fill: '#8b5cf6', r: 6 }}
                          activeDot={{ r: 8 }}
                          connectNulls
                          name="Age Difference (Years)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-200">
                      <div className="text-sm text-gray-600">2023-24</div>
                      <div className="text-2xl font-bold text-purple-600 mt-1">
                        {cambridgeAssessmentData2023?.[selectedModule]?.fullRecord?.[`${selectedModule} (Age difference)`] ? 
                          (cambridgeAssessmentData2023[selectedModule].fullRecord[`${selectedModule} (Age difference)`] > 0 ? '+' : '') + 
                          cambridgeAssessmentData2023[selectedModule].fullRecord[`${selectedModule} (Age difference)`].toFixed(2)
                          : '—'}
                      </div>
                    </div>
                    <div className="bg-violet-50 rounded-lg p-4 text-center border border-violet-200">
                      <div className="text-sm text-gray-600">2024-25</div>
                      <div className="text-2xl font-bold text-violet-600 mt-1">
                        {cambridgeAssessmentData2024?.[selectedModule]?.fullRecord?.[`${selectedModule} (Age difference)`] ? 
                          (cambridgeAssessmentData2024[selectedModule].fullRecord[`${selectedModule} (Age difference)`] > 0 ? '+' : '') + 
                          cambridgeAssessmentData2024[selectedModule].fullRecord[`${selectedModule} (Age difference)`].toFixed(2)
                          : '—'}
                      </div>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-4 text-center border border-indigo-200">
                      <div className="text-sm text-gray-600">2025-26</div>
                      <div className="text-2xl font-bold text-indigo-600 mt-1">
                        {cambridgeAssessmentData?.[selectedModule]?.fullRecord?.[`${selectedModule} (Age difference)`] ? 
                          (cambridgeAssessmentData[selectedModule].fullRecord[`${selectedModule} (Age difference)`] > 0 ? '+' : '') + 
                          cambridgeAssessmentData[selectedModule].fullRecord[`${selectedModule} (Age difference)`].toFixed(2)
                          : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
                  <h4 className="text-md font-semibold text-gray-800 mb-4">Progress Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">Overall Trend (Standardised Score)</div>
                      {cambridgeAssessmentData2023?.[selectedModule]?.score && cambridgeAssessmentData?.[selectedModule]?.score ? (
                        <div className={`text-lg font-bold mt-2 ${
                          cambridgeAssessmentData[selectedModule].score > cambridgeAssessmentData2023[selectedModule].score
                            ? 'text-green-600'
                            : cambridgeAssessmentData[selectedModule].score < cambridgeAssessmentData2023[selectedModule].score
                            ? 'text-red-600'
                            : 'text-gray-600'
                        }`}>
                          {cambridgeAssessmentData[selectedModule].score > cambridgeAssessmentData2023[selectedModule].score ? '↑' : '↓'} {Math.abs((cambridgeAssessmentData[selectedModule].score - cambridgeAssessmentData2023[selectedModule].score)).toFixed(1)} points
                        </div>
                      ) : (
                        <div className="text-gray-500 mt-2">No data for comparison</div>
                      )}
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Latest Performance Level</div>
                      <div className={`text-lg font-bold mt-2 ${getPerformanceLevel(cambridgeAssessmentData?.[selectedModule]?.score || 0).textColor}`}>
                        {getPerformanceLevel(cambridgeAssessmentData?.[selectedModule]?.score || 0).level}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "notes" && (
        <div className="mt-4 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Teacher Notes</h3>
              <button
                onClick={() => setShowAddNoteModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-medium"
              >
                📝 Add Note
              </button>
            </div>

            {showAddNoteModal && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Write your note here..."
                  rows="4"
                  className="w-full border border-gray-300 rounded px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      if (!noteText.trim()) {
                        alert('Please write a note');
                        return;
                      }
                      try {
                        console.log('Saving note for pupil:', studentId);
                        const response = await fetch(buildApiUrl(`/api/pupil-notes`), {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            pupil_id: studentId,
                            note_text: noteText,
                            teacher_email: teacherEmail || 'teacher@school.com',
                            teacher_name: teacherEmail ? teacherEmail.split('@')[0] : 'Teacher'
                          })
                        });
                        
                        const data = await response.json();
                        console.log('Save response:', data);
                        
                        if (response.ok && data.success && data.note) {
                          // Add the new note to the list
                          setNotes([data.note, ...notes]);
                          setNoteText('');
                          setShowAddNoteModal(false);
                          alert('Note saved successfully!');
                        } else {
                          console.error('Save failed:', data);
                          alert('Failed to save note: ' + (data.error || 'Unknown error'));
                        }
                      } catch (error) {
                        console.error('Error adding note:', error);
                        alert('Failed to add note: ' + error.message);
                      }
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 font-medium"
                  >
                    ✅ Save Note
                  </button>
                  <button
                    onClick={() => {
                      setShowAddNoteModal(false);
                      setNoteText('');
                    }}
                    className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500 font-medium"
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {notes && notes.length > 0 ? (
                notes.map((note) => {
                  // Defensive check for note object
                  if (!note) return null;
                  
                  return (
                    <div key={note.id || Math.random()} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="font-semibold text-gray-900">{note.teacher_name || note.teacher_email || 'Unknown Teacher'}</div>
                          <div className="text-sm text-gray-600">
                            {note.created_at ? new Date(note.created_at).toLocaleDateString() + ' at ' + new Date(note.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Unknown date'}
                          </div>
                          {note.published && (
                            <div className="text-xs text-green-600 font-semibold mt-1">✅ Published to Parents</div>
                          )}
                          {!note.published && (
                            <div className="text-xs text-orange-600 font-semibold mt-1">🔒 Private (Not shared)</div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {!note.published && (
                            <button
                              onClick={async () => {
                                try {
                                  console.log('Publishing note:', note.id);
                                  const response = await fetch(buildApiUrl(`/api/pupil-notes/${note.id}/publish`), {
                                    method: 'PUT'
                                  });
                                  const data = await response.json();
                                  console.log('Publish response:', response.status, data);
                                  if (response.ok && data.success) {
                                    setNotes(notes.map(n => n.id === note.id ? data.note : n));
                                    console.log('✅ Note published successfully');
                                  } else {
                                    console.error('❌ Publish failed:', data);
                                    alert(`Error publishing note: ${data.error || 'Unknown error'}`);
                                  }
                                } catch (error) {
                                  console.error('❌ Error publishing note:', error);
                                  alert('Error publishing note: ' + error.message);
                                }
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1 bg-blue-50 rounded hover:bg-blue-100"
                            >
                              👁️ Publish to Parents
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              if (window.confirm('Delete this note?')) {
                                try {
                                  await fetch(buildApiUrl(`/api/pupil-notes/${note.id}`), {
                                    method: 'DELETE'
                                  });
                                  setNotes(notes.filter(n => n.id !== note.id));
                                } catch (error) {
                                  console.error('Error deleting note:', error);
                                }
                              }
                            }}
                            className="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-1 bg-red-50 rounded hover:bg-red-100"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{note.note_text || note || 'No note text'}</p>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📝</div>
                  <p>No notes added yet</p>
                  <p className="text-sm mt-2">Click "Add Note" to add your first note</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Target Modal ──────────────────────────────────────────────────────── */}
      {showTargetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-900">🎯 Assessment Target</h2>
                <p className="text-sm text-gray-500">{targetModalModule?.name} — Current score: <strong>{targetModalModule?.score ?? 'N/A'}</strong></p>
              </div>
              <button onClick={() => setShowTargetModal(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
            </div>

            <div className="px-6 py-5">
              {/* Choose view */}
              {targetModalView === 'choose' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-4">What would you like to do with this assessment?</p>
                  <button
                    onClick={() => setTargetModalView('create')}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <span className="text-3xl">➕</span>
                    <div>
                      <div className="font-semibold text-gray-900">Create New Target</div>
                      <div className="text-xs text-gray-500">Set a new learning goal based on this assessment</div>
                    </div>
                  </button>
                  <button
                    onClick={() => setTargetModalView('link')}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-purple-200 hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                  >
                    <span className="text-3xl">🔗</span>
                    <div>
                      <div className="font-semibold text-gray-900">Link with Existing Target</div>
                      <div className="text-xs text-gray-500">Connect this assessment to an already-created target</div>
                    </div>
                  </button>
                </div>
              )}

              {/* Create view */}
              {targetModalView === 'create' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Title *</label>
                    <input
                      type="text"
                      value={targetForm.title}
                      onChange={e => setTargetForm(f => ({ ...f, title: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. Reach Average in Mathematics"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={targetForm.description}
                      onChange={e => setTargetForm(f => ({ ...f, description: e.target.value }))}
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="What should the student achieve?"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target Score</label>
                      <input
                        type="number"
                        min="0"
                        max="200"
                        value={targetForm.target_score}
                        onChange={e => setTargetForm(f => ({ ...f, target_score: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g. 100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={targetForm.due_date}
                        onChange={e => setTargetForm(f => ({ ...f, due_date: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => setTargetModalView('choose')} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
                    <button
                      onClick={saveNewTarget}
                      disabled={!targetForm.title || targetSaving}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                    >
                      {targetSaving ? 'Saving…' : 'Save Target'}
                    </button>
                  </div>
                </div>
              )}

              {/* Link view */}
              {targetModalView === 'link' && (
                <div className="space-y-3">
                  {targets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <div className="text-4xl mb-2">📋</div>
                      <p className="text-sm">No existing targets found for this student.</p>
                      <button onClick={() => setTargetModalView('create')} className="mt-3 text-blue-600 text-sm underline">Create one instead</button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-3">Select a target to link this assessment to:</p>
                      <div className="max-h-56 overflow-y-auto space-y-2">
                        {targets.map(t => (
                          <div
                            key={t.id}
                            onClick={() => setTargetLinkId(t.id)}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${targetLinkId === t.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}`}
                          >
                            <div className="font-medium text-sm text-gray-900">{t.title}</div>
                            {t.assessment_module && <div className="text-xs text-gray-500 mt-0.5">Currently linked to: {t.assessment_module}</div>}
                            {t.target_score && <div className="text-xs text-gray-500">Target score: {t.target_score}</div>}
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button onClick={() => setTargetModalView('choose')} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">← Back</button>
                        <button
                          onClick={linkAssessmentToTarget}
                          disabled={!targetLinkId || targetSaving}
                          className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
                        >
                          {targetSaving ? 'Linking…' : 'Link Target'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {tab === "documents" && (
        <DocumentsTab studentId={studentId} />
      )}
      {/* ────────────────────────────────────────────────────────────────────── */}
    </div>
  );

};

export default StudentPage;
